'use client';

import { initRum } from '@curology/logger/client';
import {
  GTM_DATA_LAYER_SCRIPT,
  GTMScript,
  RemoveAmplitudeDeviceIdScript,
  useGtmKey,
} from '@curology/site-tools';
import { type Brand } from '@curology/ui-components-web/brand';
import { BrandProvider } from '@curology/ui-components-web/brand/context';
import { ToastList } from '@curology/ui-components-web/react/Toast/ToastList';
import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import { Provider as JotaiProvider } from 'jotai';
import Script from 'next/script';
import { BrandStyles, ResetStyles } from 'radiance-ui';
import { primaryTheme } from 'radiance-ui/lib/constants';
import React, { useEffect, useState } from 'react';

import FlashMessages from '@/components/global/flashMessages';
import { useDecagonChatBot } from '@/hooks/useDecagonChatBot';
import { UserService } from '@/services/userService/client';
import { type AnalyticsPatientResource } from '@/types/metrics/analyticsPatient';
import { identifyUser } from '@/utils/analytics';
import { initLd, getContext } from '@/utils/featureFlags/client';
import { extractAnalyticsPatientData } from '@/utils/metrics';

import ReactQueryClientProvider from './ReactQueryClientProvider';

if (typeof window !== 'undefined') {
  // TODO: Init with site tool Amplitude Browser SDK implmentation once GTM collisions are handled

  initRum({
    env: process.env.NEXT_PUBLIC_APP_ENV,
    excludedActivityUrls: [
      /googletagmanager\.com/i,
      /analytics\.tiktok\.com/i,
      /webkit-masked-url/i,
      /ocuboost/i,
      /spotify/i,
      /cdn-cgi/i,
      /yimg/i,
    ],
  });
}

const MainContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

type Props = {
  children: React.ReactNode;
  brand: Brand;
};

const brandedProdGtmKeys: Record<Brand, string> = {
  curology: process.env.NEXT_PUBLIC_CUROLOGY_GTM_KEY!,
  agency: process.env.NEXT_PUBLIC_AGENCY_GTM_KEY!,
};

export const RootClientContainer = ({ children, brand }: Props) => {
  const [isMounted, setIsMounted] = useState(false);

  const [rootLevelPatient, setRootLevelPatient] = useState<
    AnalyticsPatientResource | undefined
  >();

  useDecagonChatBot(brand, rootLevelPatient);

  useEffect(() => {
    getContext(brand).then((context) => {
      initLd(context);
    });

    setIsMounted(true);

    UserService.getPatient(brand).then((patient) => {
      if (patient) {
        const analyticsPatient = extractAnalyticsPatientData(patient);
        identifyUser({ patient: analyticsPatient });

        setRootLevelPatient(patient);
      }
    });
  }, [brand]);

  const gtmKey = useGtmKey({ prodKey: brandedProdGtmKeys[brand] });

  // TODO: Remove ThemeProvider, BrandStyles, and ResetStyles when radiance-ui is no longer used
  return (
    <ReactQueryClientProvider>
      <BrandProvider brand={brand}>
        <JotaiProvider>
          <ThemeProvider theme={primaryTheme}>
            <BrandStyles />
            <ResetStyles />
            <Script
              id="gtmDataLayer"
              type="text/javascript"
              strategy="beforeInteractive"
            >
              {GTM_DATA_LAYER_SCRIPT}
            </Script>
            <MainContainer>{children}</MainContainer>
            {isMounted && <ToastList className="cur-left-2 md:cur-left-auto" />}
            <FlashMessages />
            <GTMScript overrideKey={gtmKey} />
            <RemoveAmplitudeDeviceIdScript />
          </ThemeProvider>
        </JotaiProvider>
      </BrandProvider>
    </ReactQueryClientProvider>
  );
};
